import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, Input } from '@angular/core';
import { L10n, L10nBase } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';

import { InventoryDocumentErrorCodes } from '@skylab-angular-shared/business';
import { ButtonComponent, DialogButtonResult, DialogContentComponentBase, DialogContentComponentIns, DialogContentViewModel, DialogHeaderComponentIns, DialogInsService, DialogState } from '@skylab-angular-shared/ui';
import { DocumentCommonErrorCodes } from '@skylab-shared/business';
import { SaveConflictResponse } from '@skylab-shared/core';
import { head } from '@skylab-shared/utils';

export enum CreateInventoryDocumentToCommercialDocumentFailedHandleOption {
    CorrectDocument,
    Retry,
    SaveDocumentWithDeferredInventoryEffect
}

@Component({
    selector: 'skl-create-inventory-document-to-commercial-document-dialog',
    templateUrl: './create-inventory-document-to-commercial-document-failed-dialog.component.html',
    styleUrls: ['./create-inventory-document-to-commercial-document-failed-dialog.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, ButtonComponent, TranslatePipe]
})
export class CreateInventoryDocumentToCommercialDocumentFailedDialogComponent extends DialogContentComponentBase<DialogContentViewModel> {
    @Input() private saveResult!: SaveConflictResponse<InventoryDocumentErrorCodes | DocumentCommonErrorCodes>;
    @Input() private kind!: 'sale' | 'purchase';

    private selected!: CreateInventoryDocumentToCommercialDocumentFailedHandleOption;;
    public options: { label: string, description: string, value: CreateInventoryDocumentToCommercialDocumentFailedHandleOption }[] = [];

    public constructor(
        dialogService: DialogInsService,
        @Inject(L10n) private readonly l10n: L10nBase
    ) {
        super(dialogService);
    }

    protected override async prepareDataAsync(): Promise<void> {
        const opts = [];
        if (!this.isInventoryError(InventoryDocumentErrorCodes.RequestedQuantityOfProductIsNotAvailable)) {
            opts.push({
                label: this.l10n._T(`scenes.shared.dialog.create-inventory-document-to-commercial-document.${this.l10nDocumentPrefix}.options.retry.label`),
                description: this.l10n._T(`scenes.shared.dialog.create-inventory-document-to-commercial-document.${this.l10nDocumentPrefix}.options.retry.description`),
                value: CreateInventoryDocumentToCommercialDocumentFailedHandleOption.Retry
            });
        }
        if (this.isInventoryError(InventoryDocumentErrorCodes.RequestedQuantityOfProductIsNotAvailable)) {
            opts.push({
                label: this.l10n._T(`scenes.shared.dialog.create-inventory-document-to-commercial-document.options.correct-document.label`),
                description: this.l10n._T(`scenes.shared.dialog.create-inventory-document-to-commercial-document.options.correct-document.description`),
                value: CreateInventoryDocumentToCommercialDocumentFailedHandleOption.CorrectDocument
            });
        }
        opts.push({
            label: this.l10n._T(`scenes.shared.dialog.create-inventory-document-to-commercial-document.options.save-with-deffered-inventory-effect.label`),
            description: this.l10n._T(`scenes.shared.dialog.create-inventory-document-to-commercial-document.options.save-with-deffered-inventory-effect.description`),
            value: CreateInventoryDocumentToCommercialDocumentFailedHandleOption.SaveDocumentWithDeferredInventoryEffect
        });
        this.options = opts;

        this.selected = head(this.options).value;
    }

    public get description(): string {
        if (this.isDocumentWithoutSignatureError) {
            return this.l10n._T(`scenes.shared.dialog.create-inventory-document-to-commercial-document.${this.l10nDocumentPrefix}.messages.document-with-manual-numbering`);
        }

        if (this.isInventoryError(InventoryDocumentErrorCodes.RequestedQuantityOfProductIsNotAvailable)) {
            return this.l10n._T(`scenes.shared.dialog.create-inventory-document-to-commercial-document.messages.requested-quantity-of-product-is-not-available`);
        }

        return this.l10n._T(`scenes.shared.dialog.create-inventory-document-to-commercial-document.${this.l10nDocumentPrefix}.messages.error`);
    }

    private isInventoryError(code: InventoryDocumentErrorCodes): boolean {
        return this.saveResult?.conflicts?.some(elem => elem.code === code) ?? false;
    }

    private get isDocumentWithoutSignatureError(): boolean {
        return this.saveResult?.conflicts?.some(elem => elem.code === DocumentCommonErrorCodes.DocumentWithoutSignature) ?? false;
    }

    public selectOption(option: CreateInventoryDocumentToCommercialDocumentFailedHandleOption): void {
        this.selected = option;
    }

    public isSelected(option: CreateInventoryDocumentToCommercialDocumentFailedHandleOption): boolean {
        return option === this.selected;
    }

    public override onCancel(): void {
        this.onAction({state: DialogState.Cancel});
    }

    public onConfirm(): void {
        this.onAction({state: DialogState.Confirm});
    }

    public override onAction(result: DialogButtonResult): void {
        const value = result.state === DialogState.Cancel ? CreateInventoryDocumentToCommercialDocumentFailedHandleOption.CorrectDocument : this.selected;
        this.dialogService.close({state: DialogState.Confirm, value});
    }

    public get l10nDocumentPrefix(): string {
        return this.kind === 'purchase' ? 'goods-receipt-to-purchase-document' : 'goods-issue-to-sale-document';
    }
}
